<template>
  <div class="cb-login">
    <h1 tabindex="-1" ref="title" class="cb-heading-4">Log In</h1>
    <p
      class="cb-login__global-error cb-body-2"
      v-show="formErrorMessage !== ''"
      aria-live="polite"
    >
      {{ formErrorMessage }}
    </p>
    <p class="cb-login__error cb-body-2">
      <span v-show="formErrors.username && formError" aria-live="polite">
        Please enter a valid email address.
        <br />
      </span>
      <span v-show="formErrors.password && formError" aria-live="polite"
        >Password must be entered.</span
      >
    </p>
    <form class="cb-login__form-container">
      <cb-text-input
        :error="$v.formData.username.$error"
        :errorText="formErrors.username"
        @blur="handleBlur('username', 'Please enter a valid email address.')"
        @focus="$v.formData.username.$reset()"
        @input="
          setFieldErrorMessage(
            'username',
            'Please enter a valid email address.',
          )
        "
        class="cb-login__form-container__login"
        id="login"
        label="Email Address"
        required
        type="text"
        v-model.trim="formData.username"
      ></cb-text-input>
      <cb-text-input
        :error="$v.formData.password.$error"
        :errorText="formErrors.password"
        @blur="handleBlur('password', 'Password must be entered.')"
        @focus="$v.formData.password.$reset()"
        @input="setFieldErrorMessage('password', 'Password must be entered.')"
        class="cb-login__form-container__password"
        id="password"
        label="Password"
        required
        type="password"
        v-model.trim="formData.password"
      ></cb-text-input>
      <div class="cb-login__container">
        <label class="cb-checkbox">
          <input
            @blur="rememberLoginFocus = false"
            @focus="rememberLoginFocus = true"
            checked="checked"
            type="checkbox"
            v-model="formData.rememberLogin"
          />
          <span
            :class="rememberLoginFocus ? 'cb-checkbox__checkmark--outline' : ''"
            class="cb-checkbox__checkmark"
          ></span>
          <span class="cb-checkbox__label cb-caption">Remember Me</span>
        </label>
        <button
          class="cb-body-1 cb-link"
          type="button"
          id="forgot-password"
          @click.prevent="handleForgotPasswordClick"
        >
          Forgot Password?
        </button>
      </div>

      <cb-button
        class="cb-login__submit-btn"
        type="submit"
        :loading="loading"
        @click="handleLogin"
        >Log In</cb-button
      >
    </form>
    <p class="cb-login__register">
      <a
        @click.prevent="handleAltLoginClick"
        tabindex="0"
        href="#"
        class="cb-body-1 cb-link"
        >Access your account using an alternative method</a
      >
    </p>
    <p class="cb-login__tos">
      <a
        @click="trackLinks"
        class="cb-link"
        href="https://www.myadt.com/usage-agreement"
        rel="noopener noreferrer"
        target="_blank"
        >Terms of Use</a
      >
      •
      <a
        @click="trackLinks"
        class="cb-link"
        href="https://www.adt.com/about-adt/legal/privacy-policy"
        rel="noopener noreferrer"
        target="_blank"
        >Privacy Policy</a
      >
    </p>
  </div>
</template>

<script>
/* global gtag */
import { required, email } from 'vuelidate/lib/validators';
import { mapActions, mapState, mapMutations } from 'vuex';
import { getCookie } from '../utility/cookie';
import TextInput from './shared/TextInput.vue';
import Button from './shared/Button.vue';

export default {
  data() {
    return {
      formData: {
        username: '',
        password: '',
        rememberLogin: false,
      },
      formErrors: {
        username: 'Please enter a valid email address.',
        password: 'Password must be entered.',
      },
      loading: false,
      formError: false,
      rememberLoginFocus: false,
    };
  },
  beforeMount() {
    this.updateFormErrorMessage('');
    const username = getCookie('username');
    if (username) {
      this.formData.username = username;
      this.formData.rememberLogin = true;
    }
  },
  mounted() {
    this.$refs.title.focus();
    document.title = 'CellBridge - Login using myADT credentials';
  },
  validations: {
    formData: {
      username: {
        email,
        required,
      },
      password: {
        required,
      },
    },
  },
  computed: {
    ...mapState({
      formErrorMessage: (state) => state.formErrorMessage,
      isModalOpen: (state) => state.modal.isOpen,
    }),
  },
  methods: {
    handleBlur(fieldName, message) {
      this.$v.formData[fieldName].$touch();
      this.setFieldErrorMessage(fieldName, message);
    },
    setFieldErrorMessage(fieldName, message) {
      if (this.$v.formData[fieldName].$invalid) {
        this.formErrors[fieldName] = message;
      } else {
        this.formErrors[fieldName] = '';
      }
    },
    handleLogin() {
      this.$v.formData.$touch();
      if (!this.$v.formData.$anyError) {
        this.loading = true;
        this.login({
          username: this.formData.username,
          password: this.formData.password,
          rememberLogin: this.formData.rememberLogin,
          loginMethod: 'myADT',
        });
        this.formError = false;
        gtag('event', 'loginClick', {
          event_label: 'log in',
          event_category: 'login',
          value: 1,
        });
      } else {
        this.formError = true;
      }
    },
    handleAltLoginClick() {
      this.updateLoginStep('cbAltLogin');
      gtag('event', 'altLoginClick', {
        event_label: 'alt login click',
        event_category: 'login',
        value: 1,
      });
    },
    handleForgotPasswordClick() {
      this.openModal({
        modalContent: 'cbForgotPassword',
        returnFocusID: 'forgot-password',
        closableOnBackgroundClick: true,
        closableOnEscapeKey: true,
        centeredOnMobile: true,
      });
      gtag('event', 'forgotPasswordClick', {
        event_label: 'forgot password',
        event_category: 'login',
        value: 1,
      });
    },
    trackLinks(evt) {
      gtag('event', 'linkClick', {
        event_label: evt.target.innerHTML.toLowerCase(),
        event_category: 'login',
        value: 1,
      });
    },
    ...mapActions(['login', 'updateFormErrorMessage', 'openModal']),
    ...mapMutations(['setHasMyADTCredentials', 'updateLoginStep']),
  },
  watch: {
    formErrorMessage() {
      if (this.formErrorMessage) {
        this.loading = false;
      }
    },
    isModalOpen() {
      if (this.isModalOpen) {
        this.loading = false;
      }
    },
  },
  components: {
    cbTextInput: TextInput,
    cbButton: Button,
  },
};
</script>

<style lang="scss" scoped>
.cb-login {
  display: flex;
  flex-direction: column;
  padding: 20px;
  height: 100%;
  box-sizing: border-box;

  .cb-heading-4 {
    width: 100%;
    text-align: center;
    margin: 8px 0 8px;
  }

  &__global-error,
  &__error {
    color: $dark-red;
    text-align: center;
  }

  &__form-container {
    display: flex;
    flex-direction: column;
    margin: 8px 0 20px;

    &__login,
    &__password {
      margin-bottom: 20px;
    }
  }

  &__container {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    margin-bottom: 36px;

    button {
      white-space: nowrap;
    }
  }

  &__submit-btn {
    width: 100%;
  }

  &__register {
    flex-grow: 1;
    text-align: center;
    max-width: 200px;
    margin: 0 auto 20px;
  }

  &__tos {
    text-align: center;
    color: $azure;
  }

  @include desktop {
    padding: 20px 32px;

    .cb-heading-4 {
      text-align: left;
    }

    &__error,
    &__global-error {
      text-align: left;
    }

    &__login {
      margin-bottom: 20px;
    }

    &__container {
      margin-bottom: 40px;
    }
  }
}
</style>
